import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import BrandWordmark from '../components/BrandWordmark';
import CleanTechBackground from '../components/CleanTechBackground';
import { useBreakpoint } from '../utils/useMediaQuery';
import { isTokenValid, clearAuthData } from '../utils/tokenUtils';

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');

export default function LoginPage() {
  const [id, setId] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showChangeModal, setShowChangeModal] = useState(false);
  const [forceChange, setForceChange] = useState(false);
  const [changeForm, setChangeForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [changeError, setChangeError] = useState('');
  const [changeLoading, setChangeLoading] = useState(false);
  const [pendingUser, setPendingUser] = useState(null);
  const navigate = useNavigate();
  const isNarrow = useBreakpoint('(max-width: 900px)');
  const isPhone = useBreakpoint('(max-width: 540px)');

  // На логине всегда ведём на главную после успешной авторизации

  useEffect(() => {
    document.title = 'Вход — ИНСЕРВ';
    // Принудительно включаем тёмную тему на странице авторизации (без изменения пользовательских настроек)
    const html = document.documentElement;
    const prevThemeAttr = html.getAttribute('data-theme');
    html.setAttribute('data-theme', 'dark');

    // Проверяем наличие токена и его валидность
    const token = localStorage.getItem('authToken');
    if (token) {
      // Если токен истек, очищаем его и показываем сообщение
      if (!isTokenValid(token)) {
        clearAuthData();
        setError('Сессия истекла. Пожалуйста, пройдите авторизацию заново.');
      } else {
        // Если токен валиден, перенаправляем на главную
        navigate('/', { replace: true });
      }
    }

    return () => {
      // возвращаем прежнее значение темы при уходе со страницы логина
      if (prevThemeAttr) html.setAttribute('data-theme', prevThemeAttr);
      else html.removeAttribute('data-theme');
    };
  }, [navigate]);

  const translateLoginError = (code) => {
    switch (code) {
      case 'id_and_password_required': return 'Укажите логин и пароль';
      case 'invalid_credentials': return 'Неверный логин или пароль';
      default: return 'Ошибка авторизации';
    }
  };

  const translateChangeError = (code) => {
    switch (code) {
      case 'all_fields_required': return 'Заполните все поля';
      case 'password_mismatch': return 'Новый пароль и подтверждение не совпадают';
      case 'weak_password': return 'Пароль не соответствует требованиям безопасности';
      case 'invalid_current_password': return 'Текущий пароль введён неверно';
      case 'user_not_found': return 'Пользователь не найден';
      case 'unauthorized':
      case 'invalid_token': return 'Сессия устарела. Перезайдите.';
      default: return 'Не удалось сменить пароль';
    }
  };

  const openChangeModal = ({ forced = false, user } = {}) => {
    setPendingUser(user || null);
    setChangeForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setChangeError('');
    setForceChange(forced);
    setShowChangeModal(true);
  };

  const handleAuthSuccess = (data, { manualTrigger = false } = {}) => {
    localStorage.setItem('authToken', data.token);
    localStorage.setItem('authUser', JSON.stringify(data.user));
    const needsReset = Boolean(data.user?.mustChangePassword);
    if (needsReset) {
      openChangeModal({ forced: true, user: data.user });
      return;
    }
    if (manualTrigger) {
      openChangeModal({ forced: false, user: data.user });
      return;
    }
    navigate('/', { replace: true });
  };

  const doLoginRequest = async (payload) => {
    const r = await fetch(`${API_BASE_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    const data = await r.json();
    if (!r.ok) {
      throw new Error(translateLoginError(data?.error));
    }
    return data;
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await doLoginRequest({ id: id.trim(), password });
      handleAuthSuccess(data);
    } catch (e2) {
      setError(e2?.message || String(e2));
    } finally {
      setLoading(false);
    }
  };

  const startManualChangeFlow = async () => {
    if (!id.trim() || !password) {
      setError('Введите логин и текущий пароль, затем нажмите «Сменить пароль».');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const data = await doLoginRequest({ id: id.trim(), password });
      handleAuthSuccess(data, { manualTrigger: true });
    } catch (e2) {
      setError(e2?.message || String(e2));
    } finally {
      setLoading(false);
    }
  };

  const submitPasswordChange = async () => {
    setChangeError('');
    if (!changeForm.currentPassword || !changeForm.newPassword || !changeForm.confirmPassword) {
      setChangeError('Заполните все поля.');
      return;
    }
    if (changeForm.newPassword !== changeForm.confirmPassword) {
      setChangeError('Новый пароль и подтверждение не совпадают.');
      return;
    }
    setChangeLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      if (!token) throw new Error('Сессия истекла. Авторизуйтесь заново.');
      const resp = await fetch(`${API_BASE_URL}/api/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          currentPassword: changeForm.currentPassword,
          newPassword: changeForm.newPassword,
          confirmPassword: changeForm.confirmPassword
        })
      });
      const raw = await resp.text();
      let data = {};
      if (raw) {
        try { data = JSON.parse(raw); }
        catch { data = { error: raw }; }
      }
      if (!resp.ok) {
        const errMessage = translateChangeError(data?.error) || (typeof data?.error === 'string' ? data.error : 'Не удалось сменить пароль');
        throw new Error(errMessage);
      }
      if (data?.user) {
        localStorage.setItem('authUser', JSON.stringify(data.user));
      }
      if (!data?.user) {
        throw new Error('Сервер не вернул данные пользователя. Попробуйте ещё раз.');
      }
      setShowChangeModal(false);
      setForceChange(false);
      setPendingUser(data.user);
      setChangeForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      navigate('/', { replace: true });
    } catch (err) {
      setChangeError(err?.message || 'Не удалось сменить пароль');
    } finally {
      setChangeLoading(false);
    }
  };

  const closeModal = () => {
    if (forceChange || changeLoading) return;
    setShowChangeModal(false);
    setChangeForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setChangeError('');
  };

  return (
    <div style={{ position: 'relative', minHeight: '100vh', display: 'grid', placeItems: 'center', background: 'var(--bg-page)', color: 'var(--text-900)', padding: isPhone ? 16 : 24 }}>
      {/* Ненавязчивая технологичная анимация на фоне */}
      <div style={{ position: 'absolute', inset: 0, zIndex: 0 }}>
        <CleanTechBackground opacity={0.35} />
      </div>

      <div style={{ position: 'relative', zIndex: 1, width: 'min(920px, 98vw)', display: 'grid', gridTemplateColumns: isNarrow ? '1fr' : '1.1fr 0.9fr', gap: isPhone ? 16 : 28, alignItems: isNarrow ? 'stretch' : 'center' }}>
        {/* Левая — бренд и приветствие */}
        <div style={{ textAlign: isNarrow ? 'center' : 'left' }}>
          <BrandWordmark fixed={false} href={null} style={{ display: 'block', width: 320, maxWidth: '70vw', color: 'var(--text-900)', margin: isNarrow ? '0 auto' : '0' }} />
          <h1 style={{ margin: '16px 0 8px 0', fontSize: isPhone ? 28 : isNarrow ? 32 : 36, lineHeight: 1.15 }}>ИНСЕРВ</h1>
          <p style={{ margin: 0, fontSize: 16, color: 'var(--text-500)' }}>
            Добро пожаловать на корпоративный портал ИНСЕРВ.
          </p>
        </div>

        {/* Правая — форма входа */}
        <form
          onSubmit={submit}
          style={{
            width: '100%',
            background: 'var(--bg-card)',
            padding: isPhone ? 18 : 24,
            borderRadius: 14,
            boxShadow: 'var(--shadow-2)',
            border: '1px solid var(--border-200)'
          }}
        >
          <h2 style={{ margin: '6px 0 16px 0', textAlign: isNarrow ? 'center' : 'left' }}>Вход в систему</h2>
          <div style={{ display: 'grid', gap: 12 }}>
            <label style={{ fontSize: 14, color: 'var(--text-700)' }}>Логин (ID пользователя):</label>
            <input
              value={id}
              onChange={e=>setId(e.target.value)}
              placeholder="например, 1001"
              autoFocus
              style={{
                padding: '12px 14px',
                background: 'transparent',
                color: 'var(--text-900)',
                border: '1px solid var(--border-200)',
                borderRadius: 10,
              }}
            />
            <label style={{ fontSize: 14, color: 'var(--text-700)' }}>Пароль:</label>
            <div style={{ display: 'flex', alignItems: 'center', border: '1px solid var(--border-200)', borderRadius: 10, padding: '0 6px', background: 'var(--bg-card)' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={e=>setPassword(e.target.value)}
                style={{
                  flex: 1,
                  padding: '12px 10px',
                  background: 'transparent',
                  color: 'var(--text-900)',
                  border: 'none',
                  outline: 'none'
                }}
              />
              <button
                type="button"
                onClick={()=>setShowPassword(v=>!v)}
                aria-label={showPassword ? 'Скрыть пароль' : 'Показать пароль'}
                style={{
                  border: 'none',
                  background: 'none',
                  padding: 6,
                  cursor: 'pointer',
                  color: 'var(--text-500)'
                }}
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  {showPassword ? (
                    <>
                      <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-5.33 0-9.8-3.28-11-8 0-1.32.32-2.57.9-3.66" />
                      <path d="M6.1 6.1A10.94 10.94 0 0 1 12 4c5.33 0 9.8 3.28 11 8-.26.96-.65 1.87-1.17 2.7" />
                      <line x1="1" y1="1" x2="23" y2="23" />
                    </>
                  ) : (
                    <>
                      <path d="M1 12s3.5-7 11-7 11 7 11 7-3.5 7-11 7-11-7-11-7Z" />
                      <circle cx="12" cy="12" r="3" />
                    </>
                  )}
                </svg>
              </button>
            </div>
            {error && <div style={{ color: 'var(--danger-500)', fontSize: 13 }}>{error}</div>}
            <button
              type="submit"
              disabled={loading}
              style={{
                padding: '12px 14px',
                background: 'var(--brand-600)',
                color: '#fff',
                border: 'none',
                borderRadius: 10,
                cursor: 'pointer',
                fontWeight: 800,
              }}
            >
              {loading ? 'Входим…' : 'Войти'}
            </button>
            <button
              type="button"
              disabled={loading}
              onClick={startManualChangeFlow}
              style={{
                padding: '10px 14px',
                background: 'transparent',
                border: '1px solid var(--border-300)',
                borderRadius: 10,
                cursor: 'pointer',
                fontWeight: 600,
                color: 'var(--text-800)'
              }}
            >
              Сменить пароль
            </button>
          </div>
        </form>
      </div>

      {showChangeModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'grid', placeItems: 'center', zIndex: 20 }}>
          <div style={{ width: 'min(420px, 92vw)', background: 'var(--bg-card)', color: 'var(--text-900)', borderRadius: 16, padding: 24, boxShadow: 'var(--shadow-3)', border: '1px solid var(--border-200)', position: 'relative' }}>
            {!forceChange && (
              <button
                onClick={closeModal}
                style={{ position: 'absolute', top: 12, right: 12, cursor: 'pointer', border: 'none', background: 'transparent', fontSize: 20 }}
                aria-label="Закрыть"
              >
                ×
              </button>
            )}
            <h3 style={{ marginTop: 0 }}>{forceChange ? 'Создайте новый пароль' : 'Смена пароля'}</h3>
            {forceChange && (
              <p style={{ marginTop: 0, fontSize: 14, color: 'var(--warning-500)' }}>
                По требованиям безопасности необходимо задать новый пароль перед первым входом.
              </p>
            )}
            {pendingUser?.name && (
              <p style={{ margin: '0 0 12px 0', fontSize: 14, color: 'var(--text-600)' }}>
                Пользователь: <strong>{pendingUser.name}</strong>
              </p>
            )}
            <div style={{ display: 'grid', gap: 10 }}>
              <label style={{ fontSize: 13, color: 'var(--text-700)' }}>Текущий пароль</label>
              <input
                type="password"
                value={changeForm.currentPassword}
                onChange={e=>setChangeForm(f=>({ ...f, currentPassword: e.target.value }))}
                style={{ padding: '10px 12px', borderRadius: 10, border: '1px solid var(--border-200)', background: 'transparent', color: 'var(--text-900)' }}
              />
              <label style={{ fontSize: 13, color: 'var(--text-700)' }}>Новый пароль</label>
              <input
                type="password"
                value={changeForm.newPassword}
                onChange={e=>setChangeForm(f=>({ ...f, newPassword: e.target.value }))}
                style={{ padding: '10px 12px', borderRadius: 10, border: '1px solid var(--border-200)', background: 'transparent', color: 'var(--text-900)' }}
              />
              <label style={{ fontSize: 13, color: 'var(--text-700)' }}>Повторите новый пароль</label>
              <input
                type="password"
                value={changeForm.confirmPassword}
                onChange={e=>setChangeForm(f=>({ ...f, confirmPassword: e.target.value }))}
                style={{ padding: '10px 12px', borderRadius: 10, border: '1px solid var(--border-200)', background: 'transparent', color: 'var(--text-900)' }}
              />
              <p style={{ fontSize: 12, color: 'var(--text-600)', margin: '4px 0 0 0' }}>
                Требования: минимум 8 символов, хотя бы одна строчная и заглавная буква, а также цифра.
              </p>
              {changeError && <div style={{ color: 'var(--danger-500)', fontSize: 13 }}>{changeError}</div>}
              <button
                onClick={submitPasswordChange}
                disabled={changeLoading}
                style={{
                  padding: '12px 14px',
                  background: 'var(--brand-600)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 10,
                  cursor: 'pointer',
                  fontWeight: 700
                }}
              >
                {changeLoading ? 'Сохраняем…' : 'Сохранить новый пароль'}
              </button>
              {!forceChange && (
                <button
                  type="button"
                  onClick={closeModal}
                  disabled={changeLoading}
                  style={{
                    padding: '10px 14px',
                    border: '1px solid var(--border-300)',
                    borderRadius: 10,
                    background: 'transparent',
                    cursor: 'pointer'
                  }}
                >
                  Отмена
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
